class MLHeader extends HTMLElement {
  connectedCallback() {
    this.searchTrigger = this.querySelector('.ml-header__search-trigger');
    this.searchClose = this.querySelectorAll('.ml-search__close, .ml-search__mobile-close');
    this.searchInput = this.querySelector('.ml-search__input');
    this.searchResults = this.querySelector('#ml-search-results');

    this.announcement = this.querySelector('[data-ml-announcement]');
    this.announcementSlides = this.querySelectorAll('[data-ml-announcement-slide]');
    this.announcementPrev = this.querySelector('.ml-announcement__arrow--prev');
    this.announcementNext = this.querySelector('.ml-announcement__arrow--next');
    this.announcementIndex = 0;
    this.announcementTimer = null;

    this.initSearch();
    this.initAnnouncement();
    this.initHeaderMetrics();
  }


  initHeaderMetrics() {
    const update = () => {
      if (window.MLProductLayout) {
        window.MLProductLayout.measureHeader();
        return;
      }
      const section = this.closest('.section-header') || document.querySelector('.section-header');
      const height = section ? Math.ceil(section.getBoundingClientRect().height) : Math.ceil(this.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--ml-sticky-header-offset', `${height}px`);
    };

    update();
    requestAnimationFrame(update);
    window.addEventListener('resize', update, { passive: true });

    if ('ResizeObserver' in window) {
      this._mlHeaderResizeObserver = new ResizeObserver(update);
      this._mlHeaderResizeObserver.observe(this.closest('.section-header') || this);
    }
  }

  initSearch() {
    if (this.searchTrigger) {
      this.searchTrigger.addEventListener('click', () => {
        document.body.classList.add('ml-search-open');

        setTimeout(() => {
          if (this.searchInput) this.searchInput.focus();
        }, 120);
      });
    }

    if (this.searchClose) {
      this.searchClose.forEach((button) => {
        button.addEventListener('click', () => {
          document.body.classList.remove('ml-search-open');
        });
      });
    }

    if (this.searchInput) {
      this.searchInput.addEventListener(
        'input',
        this.debounce(() => {
          this.getSearchResults(this.searchInput.value);
        }, 220)
      );
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        document.body.classList.remove('ml-search-open');
      }
    });
  }

  initAnnouncement() {
    if (!this.announcement || this.announcementSlides.length <= 1) return;

    const autoPlay = this.announcement.dataset.autoplay === 'true';
    const speed = Number(this.announcement.dataset.speed) || 5000;

    if (this.announcementPrev) {
      this.announcementPrev.addEventListener('click', () => {
        this.showAnnouncement(this.announcementIndex - 1);
        this.restartAnnouncement(autoPlay, speed);
      });
    }

    if (this.announcementNext) {
      this.announcementNext.addEventListener('click', () => {
        this.showAnnouncement(this.announcementIndex + 1);
        this.restartAnnouncement(autoPlay, speed);
      });
    }

    this.announcement.addEventListener('mouseenter', () => {
      this.stopAnnouncement();
    });

    this.announcement.addEventListener('mouseleave', () => {
      this.startAnnouncement(autoPlay, speed);
    });

    this.startAnnouncement(autoPlay, speed);
  }

  showAnnouncement(index) {
    if (!this.announcementSlides.length) return;

    this.announcementSlides[this.announcementIndex].classList.remove('is-active');

    this.announcementIndex =
      (index + this.announcementSlides.length) % this.announcementSlides.length;

    this.announcementSlides[this.announcementIndex].classList.add('is-active');
  }

  startAnnouncement(autoPlay, speed) {
    if (!autoPlay || this.announcementSlides.length <= 1) return;

    this.stopAnnouncement();

    this.announcementTimer = setInterval(() => {
      this.showAnnouncement(this.announcementIndex + 1);
    }, speed);
  }

  stopAnnouncement() {
    if (this.announcementTimer) {
      clearInterval(this.announcementTimer);
      this.announcementTimer = null;
    }
  }

  restartAnnouncement(autoPlay, speed) {
    this.stopAnnouncement();
    this.startAnnouncement(autoPlay, speed);
  }

  getColorSearchTerm(normalizedQuery) {
    const colorTerms = [
      'red',
      'orange',
      'green',
      'teal',
      'black',
      'beige',
      'tan',
      'yellow',
      'pink',
      'purple',
      'blue',
      'brown',
      'cream',
      'ivory',
      'gray',
      'grey'
    ];

    return colorTerms.find((color) => color.startsWith(normalizedQuery)) || '';
  }

  isBroadRugSearch(normalizedQuery) {
    const broadRugSearches = [
      'vinyl',
      'rug',
      'rugs',
      'vinyl rug',
      'vinyl rugs',
      'vintage',
      'vintage vinyl',
      'vintage vinyl rug',
      'vintage vinyl rugs'
    ];

    return (
      broadRugSearches.includes(normalizedQuery) ||
      'vinyl'.startsWith(normalizedQuery)
    );
  }

  async getSearchResults(query) {
    if (!this.searchResults) return;

    const trimmedQuery = query.trim();
    const normalizedQuery = trimmedQuery.toLowerCase();

    if (trimmedQuery.length < 2) {
      this.searchResults.innerHTML = '';
      return;
    }

    this.searchResults.innerHTML =
      '<p class="ml-search-results__loading">Searching...</p>';

    try {
      let products = [];

      const colorSearchTerm = this.getColorSearchTerm(normalizedQuery);

      if (colorSearchTerm) {
        const response = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(colorSearchTerm)}&resources[type]=query,product,collection,page&resources[limit]=9`
        );

        if (!response.ok) throw new Error('Color search request failed');

        const data = await response.json();
        products = data.resources?.results?.products || [];
      } else if (this.isBroadRugSearch(normalizedQuery)) {
        const collectionResponse = await fetch('/collections/vinyl-rugs/products.json?limit=9');

        if (!collectionResponse.ok) throw new Error('Collection request failed');

        const collectionData = await collectionResponse.json();

        products = (collectionData.products || []).map((product) => ({
          title: product.title,
          url: `/products/${product.handle}`,
          image: product.images?.[0]?.src || '',
          price: product.variants?.[0]?.price ? `$${product.variants[0].price}` : ''
        }));
      } else {
        const response = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(trimmedQuery)}&resources[type]=query,product,collection,page&resources[limit]=9`
        );

        if (!response.ok) throw new Error('Search request failed');

        const data = await response.json();
        products = data.resources?.results?.products || [];
      }

      if (!products.length) {
        this.searchResults.innerHTML = `
          <p class="ml-search-results__empty">No products found for “${this.escapeHtml(trimmedQuery)}”.</p>
        `;
        return;
      }

      this.searchResults.innerHTML = `
        <div class="ml-search-results__grid">
          ${products.map((product) => this.renderProduct(product)).join('')}
        </div>
      `;
    } catch (error) {
      this.searchResults.innerHTML = `
        <p class="ml-search-results__empty">Something went wrong. Press enter to search the site.</p>
      `;
    }
  }

  renderProduct(product) {
    const rawTitle = product.title || '';
    const title = this.escapeHtml(rawTitle);

    const cleanTitle = this.escapeHtml(
      rawTitle
        .replace(/^Vintage Vinyl Rug\s*-\s*/i, '')
        .replace(/^Vintage Vinyl Runner\s*-\s*/i, '')
        .replace(/^Round Vintage Vinyl Rug\s*-\s*/i, '')
        .replace(/^Framed Art\s*-\s*/i, '')
    );

    const productType = rawTitle.toLowerCase().includes('framed')
      ? 'FRAMED ART'
      : 'VINTAGE VINYL RUG';

    const rawPrice = product.price || '';
    const isSamplePrice =
      String(rawPrice).includes('0.50') ||
      String(rawPrice).includes('.50');

    let priceLabel = '';

    if (productType === 'VINTAGE VINYL RUG') {
      priceLabel = 'From $58';
    } else {
      priceLabel = isSamplePrice ? 'View Product' : `From ${rawPrice}`;
    }

    const url = product.url || '#';
    const image = product.image || product.featured_image || '';

    return `
      <a href="${url}" class="ml-search-product">
        ${
          image
            ? `
              <div class="ml-search-product__image">
                <img src="${image}" alt="${title}" loading="lazy">
              </div>
            `
            : ''
        }

        <div class="ml-search-product__content">
          <small class="ml-search-product__type">${productType}</small>
          <strong class="ml-search-product__title">${cleanTitle}</strong>
          <span class="ml-search-product__price">${priceLabel}</span>
        </div>
      </a>
    `;
  }

  debounce(callback, delay) {
    let timeout;

    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => callback.apply(this, args), delay);
    };
  }

  escapeHtml(string) {
    return String(string).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    })[character]);
  }
}

customElements.define('ml-header', MLHeader);